import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Container, Spinner, Alert } from "react-bootstrap";
import Header from "./Header";
import FilePreviewModal, { getFileType } from "./FilePreviewModal";
import { getCachedData, setCachedData } from "../utils/apiCache";
import { ProductContext } from "../context/AppContext";

const S3_BASE = "https://erish-school-app.s3.ap-south-1.amazonaws.com/";

// The API sends a pre-signed `uploadfileUrl`; `uploadfile` is only the raw S3
// key and is used as a fallback when the signed url is missing.
const getFileUrl = (item) => {
    const signed = String(item?.uploadfileUrl || "").trim();
    if (signed && signed !== "null") return signed;

    const key = String(item?.uploadfile || "").trim();
    if (!key || key === "null") return null;
    if (/^https?:\/\//i.test(key)) return key;
    return `${S3_BASE}${key.replace(/^\/+/, "")}`;
};

const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const toDateKey = (value) => {
    if (!value) return null;
    const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) return match[1];
    const d = new Date(value);
    if (isNaN(d.getTime())) return null;
    return `${d.getFullYear()}-${`${d.getMonth() + 1}`.padStart(2, "0")}-${`${d.getDate()}`.padStart(2, "0")}`;
};

const SUBJECT_THEMES = [
    { bg: "#eef2ff", text: "#4338ca", stripe: "#818cf8" },
    { bg: "#ecfdf5", text: "#047857", stripe: "#34d399" },
    { bg: "#eff6ff", text: "#1d4ed8", stripe: "#60a5fa" },
    { bg: "#fff7ed", text: "#c2410c", stripe: "#fb923c" },
    { bg: "#faf5ff", text: "#7c3aed", stripe: "#a78bfa" },
    { bg: "#fef2f2", text: "#b91c1c", stripe: "#f87171" },
];

// Familiar subjects keep a fixed colour; anything else gets a stable colour
// derived from its name so the same subject always looks the same.
const getSubjectTheme = (name) => {
    const key = String(name || "").toLowerCase();
    if (key.includes("math")) return SUBJECT_THEMES[0];
    if (key.includes("eng") || key.includes("lang") || key.includes("hindi")) return SUBJECT_THEMES[1];
    if (key.includes("sci") || key.includes("bio") || key.includes("chem") || key.includes("phys")) return SUBJECT_THEMES[2];
    if (key.includes("social") || key.includes("hist") || key.includes("geo")) return SUBJECT_THEMES[3];
    if (key.includes("comp") || key.includes("it")) return SUBJECT_THEMES[4];

    let hash = 0;
    for (let i = 0; i < key.length; i += 1) hash += key.charCodeAt(i);
    return SUBJECT_THEMES[hash % SUBJECT_THEMES.length];
};

const getSubjectInitials = (name) => {
    const clean = String(name || "").replace(/[^a-zA-Z ]/g, " ").trim();
    if (!clean) return "SY";
    const words = clean.split(/\s+/);
    if (words.length > 1) return (words[0][0] + words[1][0]).toUpperCase();
    return clean.slice(0, 2).toUpperCase();
};

const SyllabusPage = () => {
    const [syllabus, setSyllabus] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [showFileModal, setShowFileModal] = useState(false);
    const [selectedFileUrl, setSelectedFileUrl] = useState("");
    const [selectedFileTitle, setSelectedFileTitle] = useState("");

    // Filters
    const [searchText, setSearchText] = useState("");
    const [showFilters, setShowFilters] = useState(false);
    const [selectedSubject, setSelectedSubject] = useState("");
    const [selectedFileFilter, setSelectedFileFilter] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const { GetstudentData } = useContext(ProductContext);
    const authtoken = localStorage.getItem("token");

    const classSectionId =
        GetstudentData?.class_section_id ||
        GetstudentData?.cls_sec?.id ||
        GetstudentData?.cls_sec_id;

    useEffect(() => {
        if (!classSectionId) return;

        const cacheKey = `syllabus_data_${classSectionId}`;
        const cached = getCachedData(cacheKey);
        if (cached) {
            setSyllabus(cached);
            setLoading(false);
        } else {
            setLoading(true);
        }

        let ignore = false;
        axios
            .get(`https://api.mirai.healthcare/api/head/syllabus/classSection/${classSectionId}`, {
                headers: { "x-auth": authtoken },
            })
            .then((response) => {
                if (ignore) return;
                const payload = response?.data;
                const data = Array.isArray(payload?.data)
                    ? payload.data
                    : Array.isArray(payload?.syllabus)
                        ? payload.syllabus
                        : Array.isArray(payload)
                            ? payload
                            : [];
                const sorted = [...data].sort(
                    (a, b) => new Date(b?.date || b?.created_at || 0) - new Date(a?.date || a?.created_at || 0)
                );
                setSyllabus(sorted);
                setError("");
                setCachedData(cacheKey, sorted);
            })
            .catch((err) => {
                if (ignore) return;
                console.error("Error fetching syllabus:", err);
                setError("Unable to load the syllabus right now. Please try again.");
            })
            .finally(() => {
                if (!ignore) setLoading(false);
            });

        return () => {
            ignore = true;
        };
    }, [classSectionId, authtoken]);

    const subjectOptions = useMemo(() => {
        const names = syllabus.map((item) => item?.subject?.name).filter(Boolean);
        return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
    }, [syllabus]);

    const filteredSyllabus = useMemo(() => {
        const search = searchText.trim().toLowerCase();

        return syllabus.filter((item) => {
            if (search) {
                const haystack = [item?.title, item?.description, item?.subject?.name, item?.paper?.name]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                if (!haystack.includes(search)) return false;
            }

            if (selectedSubject && item?.subject?.name !== selectedSubject) return false;

            if (selectedFileFilter) {
                const type = getFileType(getFileUrl(item));
                if (selectedFileFilter === "pdf" && type !== "pdf") return false;
                if (selectedFileFilter === "image" && type !== "image") return false;
            }

            const itemKey = toDateKey(item?.date || item?.created_at);
            if (fromDate && (!itemKey || itemKey < fromDate)) return false;
            if (toDate && (!itemKey || itemKey > toDate)) return false;

            return true;
        });
    }, [syllabus, searchText, selectedSubject, selectedFileFilter, fromDate, toDate]);

    // Changing a filter restarts the list from the first page.
    useEffect(() => {
        setCurrentPage(1);
    }, [searchText, selectedSubject, selectedFileFilter, fromDate, toDate, itemsPerPage]);

    const totalPages = Math.ceil(filteredSyllabus.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;

    const currentItems = useMemo(
        () => filteredSyllabus.slice(indexOfFirstItem, indexOfLastItem),
        [filteredSyllabus, indexOfFirstItem, indexOfLastItem]
    );

    const handlePageChange = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    const getPageNumbers = () => {
        const pageNumbers = [];
        const maxVisiblePages = 5;
        let startPage = Math.max(1, currentPage - 2);
        const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

        if (endPage - startPage < maxVisiblePages - 1) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        for (let i = startPage; i <= endPage; i += 1) pageNumbers.push(i);
        return pageNumbers;
    };

    // Only the current page is grouped, so a subject block never spans pages.
    const groupedSyllabus = useMemo(() => {
        const map = new Map();
        const result = [];

        currentItems.forEach((item, index) => {
            const subjectName = item?.subject?.name?.trim();
            if (subjectName) {
                if (!map.has(subjectName)) {
                    const groupObj = { hasSubject: true, subjectName, items: [] };
                    map.set(subjectName, groupObj);
                    result.push(groupObj);
                }
                map.get(subjectName).items.push(item);
            } else {
                result.push({
                    hasSubject: false,
                    key: `nosubject_${item?.id || index}`,
                    items: [item],
                });
            }
        });

        return result;
    }, [currentItems]);

    const activeFilterCount =
        (selectedSubject ? 1 : 0) + (selectedFileFilter ? 1 : 0) + (fromDate ? 1 : 0) + (toDate ? 1 : 0);

    const handleResetFilters = () => {
        setSelectedSubject("");
        setSelectedFileFilter("");
        setFromDate("");
        setToDate("");
        setSearchText("");
    };

    const handleOpenFile = (url, title) => {
        setSelectedFileUrl(url);
        setSelectedFileTitle(title || "Syllabus");
        setShowFileModal(true);
    };

    return (
        <div className="page-wrapper text-start" style={{ backgroundColor: "#f8fafc", minHeight: "100vh" }}>
            <style>{`
        .syllabus-banner {
          background: linear-gradient(135deg, #ede9fe 0%, #c7d2fe 100%);
          border-radius: 16px;
          box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
          min-height: 140px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          position: relative;
          overflow: hidden;
        }
        .syllabus-banner-text { padding: 24px; z-index: 2; max-width: 62%; }
        .syllabus-banner-image {
          height: 140px; width: 160px; background: #ffffff;
          border-top-left-radius: 35px; border-bottom-left-radius: 35px;
          display: flex; align-items: center; justify-content: center; overflow: hidden; z-index: 2;
        }
        .syllabus-stat {
          background: #ffffff; border: 1px solid #eef2f7; border-radius: 12px;
          padding: 10px 14px; flex: 1; min-width: 0;
        }
        .syllabus-subject-card {
          background: #ffffff; border: 1px solid #eef2f7; border-radius: 16px;
          overflow: hidden; box-shadow: 0 1px 3px rgba(15,23,42,0.04);
        }
        .syllabus-row {
          display: flex; align-items: flex-start; gap: 12px; padding: 14px 16px;
          border-top: 1px solid #f1f5f9; transition: background-color .15s ease;
        }
        .syllabus-row:hover { background-color: #f8fafc; }
        .syllabus-row-icon {
          width: 40px; height: 40px; border-radius: 10px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center; font-size: 17px;
        }
        .syllabus-chip {
          display: inline-flex; align-items: center; gap: 4px;
          background: #f1f5f9; color: #475569; border-radius: 20px;
          padding: 2px 10px; font-size: 11px; font-weight: 600;
        }
        @media (max-width: 575.98px) {
          .syllabus-banner { min-height: 110px; }
          .syllabus-banner-text { padding: 16px; max-width: 60%; }
          .syllabus-banner-image { width: 110px; height: 110px; border-top-left-radius: 20px; border-bottom-left-radius: 20px; }
          .syllabus-row { flex-wrap: wrap; }
          .syllabus-row .syllabus-row-action { width: auto; margin-top: 4px; }
          .syllabus-row .syllabus-row-action .btn {
            width: auto !important;
            font-size: 11px !important;
            padding: 3px 10px !important;
            border-radius: 6px !important;
          }
          .pagination .page-link {
            width: 28px !important;
            height: 28px !important;
            font-size: 11px !important;
          }
        }
      `}</style>

            <Header />

            <Container className="py-4" style={{ maxWidth: "1000px", paddingBottom: "100px" }}>
                {/* Banner */}
                <div className="syllabus-banner mb-4">
                    <div className="syllabus-banner-text">
                        <span className="text-secondary fw-semibold fs-13 d-block mb-1">Everything you need to study</span>
                        <h2 className="fw-bold mb-1" style={{ color: "#4f46e5", fontSize: "26px" }}>Syllabus</h2>
                        <span className="text-muted fs-13 d-block">Subject wise course plan shared by your school 📘</span>
                    </div>
                    <div className="syllabus-banner-image">
                        <img
                            src="/assets/img/boy-study.png"
                            alt="Syllabus"
                            style={{ width: "100%", height: "100%", objectFit: "contain" }}
                        />
                    </div>
                </div>

                {error && <Alert variant="danger" className="text-center">{error}</Alert>}

                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="text-muted small mt-2 mb-0">Loading syllabus...</p>
                    </div>
                ) : (
                    <>
                        {/* Quick summary */}
                        {syllabus.length > 0 && (
                            <div className="d-flex gap-2 mb-3">
                                <div className="syllabus-stat">
                                    <span className="text-muted d-block" style={{ fontSize: "11px" }}>Total Syllabus</span>
                                    <strong className="text-dark fs-15">{syllabus.length}</strong>
                                </div>
                                <div className="syllabus-stat">
                                    <span className="text-muted d-block" style={{ fontSize: "11px" }}>Subjects</span>
                                    <strong className="text-dark fs-15">{subjectOptions.length}</strong>
                                </div>
                                <div className="syllabus-stat">
                                    <span className="text-muted d-block" style={{ fontSize: "11px" }}>Showing</span>
                                    <strong className="text-dark fs-15">{filteredSyllabus.length}</strong>
                                </div>
                            </div>
                        )}

                        {/* Search & filter */}
                        {syllabus.length > 0 && (
                            <div className="mb-4">
                                <div className="d-flex align-items-center gap-2">
                                    <div
                                        className="input-group border shadow-none"
                                        style={{ borderRadius: "10px", overflow: "hidden", background: "#ffffff" }}
                                    >
                                        <span className="input-group-text bg-transparent border-0 text-muted pe-1">
                                            <i className="ti ti-search fs-18" />
                                        </span>
                                        <input
                                            type="text"
                                            className="form-control bg-transparent border-0 fs-14 py-2 ps-1 text-dark"
                                            placeholder="Search by title, subject or paper..."
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            style={{ boxShadow: "none" }}
                                        />
                                        {searchText && (
                                            <button
                                                type="button"
                                                className="btn btn-link text-muted px-2"
                                                onClick={() => setSearchText("")}
                                                title="Clear search"
                                            >
                                                <i className="ti ti-x fs-16" />
                                            </button>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-outline-light border d-flex align-items-center justify-content-center position-relative"
                                        onClick={() => setShowFilters(!showFilters)}
                                        style={{
                                            width: "42px",
                                            height: "42px",
                                            borderRadius: "10px",
                                            borderColor: "#e2e8f0",
                                            backgroundColor: showFilters ? "#e2e8f0" : "white",
                                            color: "#475569",
                                        }}
                                        title="Toggle Filters"
                                    >
                                        <i className="ti ti-adjustments-horizontal fs-18" />
                                        {activeFilterCount > 0 && (
                                            <span
                                                className="position-absolute badge rounded-pill bg-primary"
                                                style={{ top: "-6px", right: "-6px", fontSize: "9px" }}
                                            >
                                                {activeFilterCount}
                                            </span>
                                        )}
                                    </button>
                                </div>

                                {showFilters && (
                                    <div
                                        className="card border-0 shadow-sm mt-3 p-3"
                                        style={{ borderRadius: "12px", background: "#ffffff", border: "1px solid #e2e8f0" }}
                                    >
                                        <div className="row g-3 align-items-end">
                                            <div className="col-12 col-md-3">
                                                <label className="form-label text-secondary fw-semibold fs-12 mb-1">Subject</label>
                                                <select
                                                    className="form-select fs-13"
                                                    value={selectedSubject}
                                                    onChange={(e) => setSelectedSubject(e.target.value)}
                                                    style={{ height: "38px", borderRadius: "6px", border: "1px solid #cbd5e1" }}
                                                >
                                                    <option value="">All Subjects</option>
                                                    {subjectOptions.map((name) => (
                                                        <option key={name} value={name}>{name}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="col-12 col-md-3">
                                                <label className="form-label text-secondary fw-semibold fs-12 mb-1">File Type</label>
                                                <select
                                                    className="form-select fs-13"
                                                    value={selectedFileFilter}
                                                    onChange={(e) => setSelectedFileFilter(e.target.value)}
                                                    style={{ height: "38px", borderRadius: "6px", border: "1px solid #cbd5e1" }}
                                                >
                                                    <option value="">All Files</option>
                                                    <option value="pdf">PDF</option>
                                                    <option value="image">Image</option>
                                                </select>
                                            </div>
                                            <div className="col-6 col-md-2">
                                                <label className="form-label text-secondary fw-semibold fs-12 mb-1">From Date</label>
                                                <input
                                                    type="date"
                                                    className="form-control fs-13"
                                                    value={fromDate}
                                                    onChange={(e) => setFromDate(e.target.value)}
                                                    style={{ height: "38px", borderRadius: "6px", border: "1px solid #cbd5e1" }}
                                                />
                                            </div>
                                            <div className="col-6 col-md-2">
                                                <label className="form-label text-secondary fw-semibold fs-12 mb-1">To Date</label>
                                                <input
                                                    type="date"
                                                    className="form-control fs-13"
                                                    value={toDate}
                                                    onChange={(e) => setToDate(e.target.value)}
                                                    style={{ height: "38px", borderRadius: "6px", border: "1px solid #cbd5e1" }}
                                                />
                                            </div>
                                            <div className="col-12 col-md-2">
                                                <button
                                                    type="button"
                                                    className="btn btn-light btn-sm w-100"
                                                    onClick={handleResetFilters}
                                                    style={{ height: "38px", borderRadius: "6px", border: "1px solid #cbd5e1" }}
                                                >
                                                    Reset
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Syllabus grouped by subject */}
                        {groupedSyllabus.length > 0 ? (
                            <div className="d-flex flex-column gap-3">
                                {groupedSyllabus.map((group, groupIdx) => {
                                    const hasSubjectHeader = group.hasSubject;
                                    const theme = hasSubjectHeader ? getSubjectTheme(group.subjectName) : null;

                                    return (
                                        <div
                                            key={hasSubjectHeader ? `subj_${group.subjectName}` : group.key || `nosubj_${groupIdx}`}
                                            className="syllabus-subject-card"
                                            style={hasSubjectHeader ? { borderLeft: `5px solid ${theme.stripe}` } : {}}
                                        >
                                            {hasSubjectHeader && (
                                                <div className="d-flex align-items-center gap-3 p-3">
                                                    <div
                                                        className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 fw-bold"
                                                        style={{
                                                            width: "42px",
                                                            height: "42px",
                                                            backgroundColor: theme.bg,
                                                            color: theme.text,
                                                            fontSize: "13px",
                                                        }}
                                                    >
                                                        {getSubjectInitials(group.subjectName)}
                                                    </div>
                                                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                                        <h5 className="fw-bold text-dark mb-0 text-capitalize" style={{ fontSize: "15px" }}>
                                                            {group.subjectName}
                                                        </h5>
                                                    </div>
                                                </div>
                                            )}

                                            {group.items.map((item, idx) => {
                                                const fileUrl = getFileUrl(item);
                                                const fileType = getFileType(fileUrl);
                                                const publishedOn = formatDate(item?.date || item?.created_at);

                                                return (
                                                    <div className="syllabus-row" key={item?.id || idx}>
                                                        <div
                                                            className="syllabus-row-icon"
                                                            style={{
                                                                backgroundColor: fileType === "pdf" ? "#fef2f2" : fileType === "image" ? "#eff6ff" : "#f8fafc",
                                                                color: fileType === "pdf" ? "#dc2626" : fileType === "image" ? "#2563eb" : "#64748b",
                                                            }}
                                                        >
                                                            <i
                                                                className={
                                                                    fileType === "pdf"
                                                                        ? "ti ti-file-type-pdf"
                                                                        : fileType === "image"
                                                                            ? "ti ti-photo"
                                                                            : "ti ti-file-text"
                                                                }
                                                            />
                                                        </div>

                                                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                                            <h6 className="fw-bold text-dark mb-1 text-capitalize" style={{ fontSize: "14px" }}>
                                                                {item?.title || "Syllabus"}
                                                            </h6>
                                                            {item?.description && (
                                                                <p className="text-muted mb-2" style={{ fontSize: "12px", lineHeight: 1.45 }}>
                                                                    {item.description}
                                                                </p>
                                                            )}
                                                            <div className="d-flex align-items-center flex-wrap gap-2">
                                                                {item?.paper?.name && (
                                                                    <span className="syllabus-chip text-capitalize">
                                                                        <i className="ti ti-book-2" />
                                                                        {item.paper.name}
                                                                    </span>
                                                                )}
                                                                {publishedOn && (
                                                                    <span className="syllabus-chip">
                                                                        <i className="ti ti-calendar-event" />
                                                                        {publishedOn}
                                                                    </span>
                                                                )}
                                                                {fileUrl && (
                                                                    <span className="syllabus-chip text-uppercase">
                                                                        {fileType === "pdf" ? "PDF" : fileType === "image" ? "Image" : "File"}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div className="syllabus-row-action ms-auto">
                                                            {fileUrl ? (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-primary btn-sm d-inline-flex align-items-center justify-content-center gap-1"
                                                                    style={{ borderRadius: "8px", fontWeight: 600, fontSize: "12px" }}
                                                                    onClick={() => handleOpenFile(fileUrl, item?.title)}
                                                                >
                                                                    <i className="ti ti-eye fs-14" />
                                                                    View
                                                                </button>
                                                            ) : (
                                                                <span className="text-muted" style={{ fontSize: "11px" }}>No file</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="text-center py-5 card border-0 shadow-sm" style={{ borderRadius: "16px" }}>
                                <i className="ti ti-notebook-off text-muted mb-2" style={{ fontSize: "42px" }} />
                                <h6 className="fw-bold text-dark mb-1">
                                    {syllabus.length === 0 ? "No Syllabus Available" : "No Matching Syllabus"}
                                </h6>
                                <p className="text-muted small mb-3 px-3">
                                    {syllabus.length === 0
                                        ? classSectionId
                                            ? "The school has not published a syllabus for your class yet."
                                            : "Class details are still loading. Please wait a moment."
                                        : "Try changing the search or filters to see more results."}
                                </p>
                                {syllabus.length > 0 && (
                                    <div>
                                        <button type="button" className="btn btn-outline-primary btn-sm" onClick={handleResetFilters}>
                                            Clear Filters
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Pagination footer */}
                        {filteredSyllabus.length > 0 && (
                            <div
                                className="d-flex flex-column flex-sm-row align-items-center justify-content-between gap-3 mt-4 pt-3 mb-4 pb-4 border-top w-100"
                                style={{ marginBottom: "60px" }}
                            >
                                <div className="text-secondary fs-12 text-center text-sm-start">
                                    Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredSyllabus.length)} of {filteredSyllabus.length} entries
                                </div>

                                <div className="d-flex flex-wrap align-items-center justify-content-center gap-2 max-w-100 overflow-x-auto">
                                    <nav aria-label="Page navigation" className="mb-0">
                                        <ul className="pagination pagination-sm mb-0" style={{ gap: "4px", flexWrap: "wrap", justifyContent: "center" }}>
                                            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                                <button
                                                    type="button"
                                                    className="page-link border-0 rounded-circle d-flex align-items-center justify-content-center"
                                                    style={{ width: "32px", height: "32px", backgroundColor: "#f1f5f9", color: "#64748b" }}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handlePageChange(currentPage - 1);
                                                    }}
                                                >
                                                    <i className="ti ti-chevron-left fs-12" />
                                                </button>
                                            </li>
                                            {getPageNumbers().map((page) => (
                                                <li key={page} className={`page-item ${currentPage === page ? "active" : ""}`}>
                                                    <button
                                                        type="button"
                                                        className="page-link border-0 rounded-circle d-flex align-items-center justify-content-center"
                                                        style={{
                                                            width: "32px",
                                                            height: "32px",
                                                            backgroundColor: currentPage === page ? "#4f46e5" : "#f1f5f9",
                                                            color: currentPage === page ? "#ffffff" : "#64748b",
                                                            fontWeight: "600",
                                                        }}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            handlePageChange(page);
                                                        }}
                                                    >
                                                        {page}
                                                    </button>
                                                </li>
                                            ))}
                                            <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? "disabled" : ""}`}>
                                                <button
                                                    type="button"
                                                    className="page-link border-0 rounded-circle d-flex align-items-center justify-content-center"
                                                    style={{ width: "32px", height: "32px", backgroundColor: "#f1f5f9", color: "#64748b" }}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handlePageChange(currentPage + 1);
                                                    }}
                                                >
                                                    <i className="ti ti-chevron-right fs-12" />
                                                </button>
                                            </li>
                                        </ul>
                                    </nav>

                                    {/* Items Per Page Select */}
                                    <select
                                        className="form-select form-select-sm border-light ms-sm-2"
                                        value={itemsPerPage}
                                        onChange={(e) => {
                                            setItemsPerPage(Number(e.target.value));
                                            setCurrentPage(1);
                                        }}
                                        style={{ width: "110px", height: "32px", fontSize: "12px", borderRadius: "6px" }}
                                    >
                                        <option value={5}>5 / page</option>
                                        <option value={10}>10 / page</option>
                                        <option value={20}>20 / page</option>
                                        <option value={50}>50 / page</option>
                                    </select>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </Container>

            <FilePreviewModal
                show={showFileModal}
                onHide={() => setShowFileModal(false)}
                url={selectedFileUrl}
                title={selectedFileTitle}
                fallbackFileName="syllabus"
            />
        </div>
    );
};

export default SyllabusPage;
